import {button,escapeHtml,message} from '../ui/components.js';
import {stageDisplayName,stageTypeOptions} from './stage.js';
import {temperatureFromCelsius} from './stage-target.js';

function optionMarkup(options,selected){
  return options.map(option=>
    '<option value="'+escapeHtml(option.key)+'"'+(option.key===selected?' selected':'')+'>'+
    escapeHtml(option.label)+'</option>'
  ).join('');
}

function numberValue(value){
  return value==null?'':String(value);
}

function durationLabel(minutes){
  const value=Number(minutes);
  if(!Number.isFinite(value)||value<=0)return null;
  if(value%1440===0)return (value/1440)+' d';
  if(value%60===0)return (value/60)+' h';
  if(value>60)return Math.floor(value/60)+' h '+(value%60)+' min';
  return value+' min';
}

function targetTemperature(value,unit){
  const presented=temperatureFromCelsius(value,{unit});
  return presented==null?null:String(presented)+'°'+unit;
}

function stageMeta(summary,unit){
  const target=summary?.target||{};
  const parts=[];
  const chamber=targetTemperature(target.targetChamberCelsius,unit);
  if(chamber)parts.push('Komora '+chamber);

  const min=targetTemperature(target.minChamberCelsius,unit);
  const max=targetTemperature(target.maxChamberCelsius,unit);
  if(min||max)parts.push('Zakres '+(min||'—')+' – '+(max||'—'));

  const duration=durationLabel(target.plannedDurationMinutes);
  if(duration)parts.push(duration);
  if(summary?.itemTargetCount)parts.push(summary.itemTargetCount+' cel(e) produktu');
  return parts.join(' · ')||'Bez celów — etap ręczny';
}

function stageCard(summary,index,total,editable,unit){
  const stage=summary.stage;
  const controls=editable
    ?'<div class="actions compact">'+
      button({label:'Edytuj',variant:'secondary',attributes:{'data-journal-stage-edit':stage.id}})+
      button({label:'↑',variant:'ghost',disabled:index===0,attributes:{'data-journal-stage-up':stage.id}})+
      button({label:'↓',variant:'ghost',disabled:index===total-1,attributes:{'data-journal-stage-down':stage.id}})+
      button({label:'Usuń',variant:'danger',attributes:{'data-journal-stage-remove':stage.id}})+
      '</div>'
    :'';
  return '<article class="panel journal-stage-card"><div class="journal-stage-number">'+(index+1)+'</div>'+
    '<div class="journal-stage-copy"><strong>'+escapeHtml(summary.displayName||stageDisplayName(stage))+
    '</strong><p>'+escapeHtml(stageMeta(summary,unit))+'</p></div>'+controls+'</article>';
}

function itemTargetFields(items,editingItemTargets,unit){
  const byId=new Map((editingItemTargets||[]).map(row=>[row.itemId,row.targetInternalCelsius]));
  if(!(items||[]).length)return '<p class="panel-copy">Dodaj produkty, aby ustawić temperaturę wewnętrzną dla konkretnego produktu.</p>';
  return '<div class="journal-stage-item-targets">'+(items||[]).map(item=>{
    const current=temperatureFromCelsius(byId.get(item.id),{unit});
    return '<label class="field"><span class="field-label">'+escapeHtml(item.nameSnapshot||item.name||'Produkt')+
      ' — temperatura wewnętrzna °'+unit+'</span>'+
      '<input name="internalTarget:'+escapeHtml(item.id)+'" type="number" step="0.1" value="'+
      escapeHtml(numberValue(current))+'"></label>';
  }).join('')+'</div>';
}

export function smokingStageEditorMarkup({
  summaries=[],
  items=[],
  editingStage=null,
  editingTarget=null,
  editingItemTargets=[],
  temperatureUnit='C',
  locale='pl',
  offline=false,
  sessionStatus='draft'
}={}){
  const unit=String(temperatureUnit||'C').toUpperCase()==='F'?'F':'C';
  const editable=['draft','planned','preparation','ready'].includes(String(sessionStatus||'').toLowerCase());
  const list=(summaries||[]).length
    ?summaries.map((summary,index)=>stageCard(summary,index,summaries.length,editable,unit)).join('')
    :'<p class="panel-copy">Dodaj pierwszy etap, aby ułożyć plan procesu.</p>';

  const lock=editable?'':message({
    text:'Plan etapów jest zablokowany po rozpoczęciu właściwego procesu wędzenia.',
    kind:'warning'
  });
  const offlineNotice=offline?message({
    text:'Pracujesz offline. Plan zostanie zapisany lokalnie i zsynchronizowany po odzyskaniu połączenia.',
    kind:'warning'
  }):'';

  const presented={
    targetChamber:temperatureFromCelsius(editingTarget?.targetChamberCelsius,{unit}),
    minChamber:temperatureFromCelsius(editingTarget?.minChamberCelsius,{unit}),
    maxChamber:temperatureFromCelsius(editingTarget?.maxChamberCelsius,{unit})
  };

  const form=editable
    ?'<form id="smoking-stage-form" class="smoker-form journal-stage-form" novalidate>'+offlineNotice+
      '<div class="smoker-form-section"><h2>'+(editingStage?'Edytuj etap':'Dodaj etap')+'</h2>'+
      '<div class="form-grid">'+
      '<label class="field"><span class="field-label">Typ etapu</span><select name="stageType" required>'+
      '<option value="">Wybierz…</option>'+
      optionMarkup(stageTypeOptions({locale}),editingStage?.typeKey||'')+'</select></label>'+
      '<label class="field"><span class="field-label">Nazwa własna (wymagana dla etapu własnego)</span>'+
      '<input name="stageName" maxlength="160" value="'+escapeHtml(editingStage?.nameOverride||'')+'"></label>'+
      '<label class="field"><span class="field-label">Jednostka temperatury</span><select name="temperatureUnit">'+
      '<option value="C"'+(unit==='C'?' selected':'')+'>°C</option>'+
      '<option value="F"'+(unit==='F'?' selected':'')+'>°F</option></select></label>'+
      '<label class="field"><span class="field-label">Cel komory °'+unit+'</span>'+
      '<input name="targetChamber" type="number" step="0.1" value="'+escapeHtml(numberValue(presented.targetChamber))+'"></label>'+
      '<label class="field"><span class="field-label">Minimum komory °'+unit+'</span>'+
      '<input name="minChamber" type="number" step="0.1" value="'+escapeHtml(numberValue(presented.minChamber))+'"></label>'+
      '<label class="field"><span class="field-label">Maksimum komory °'+unit+'</span>'+
      '<input name="maxChamber" type="number" step="0.1" value="'+escapeHtml(numberValue(presented.maxChamber))+'"></label>'+
      '<label class="field"><span class="field-label">Planowany czas (min)</span>'+
      '<input name="plannedDurationMinutes" type="number" min="1" step="1" value="'+
      escapeHtml(numberValue(editingTarget?.plannedDurationMinutes))+'"></label>'+
      '</div></div>'+
      '<div class="smoker-form-section"><h3>Cele produktów</h3>'+
      itemTargetFields(items,editingItemTargets,unit)+'</div>'+
      '<div class="actions">'+button({label:editingStage?'Zapisz etap':'Dodaj etap',type:'submit'})+
      (editingStage?button({id:'cancel-smoking-stage-edit',label:'Anuluj',variant:'ghost'}):'')+
      '</div></form>'
    :'';

  return '<section class="journal-stage-editor">'+lock+
    '<div class="journal-stage-summary" aria-label="Plan etapów">'+list+'</div>'+form+'</section>';
}
